/**
 * React hooks for PostHog feature flags.
 *
 * Flags arrive asynchronously (after the initial /flags request resolves), so
 * these subscribe via onFeatureFlags and re-render when values land or change.
 * They read safe defaults until PostHog is initialized, so components using
 * them render fine in dev, in tests, and before consent is granted.
 *
 *   const showNewCheckout = useFeatureFlag('new-checkout')          // boolean
 *   const variant = useFeatureFlagVariant('pricing-experiment')     // string | boolean | undefined
 *   const variant = useFeatureFlagVariant('x', { enabled: eligible }) // undefined while not eligible
 *   const ready = useFeatureFlagsReady(1000)                        // flags landed, or gave up
 */
import { useEffect, useState } from 'react'
import {
  isFeatureEnabled,
  getFeatureFlag,
  onFeatureFlags,
  isPosthogReady,
  remoteConfigHasNoFlags
} from './posthog'

// Boolean flag: true only when the flag is enabled for this visitor.
export const useFeatureFlag = (flag) => {
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flag))
  useEffect(() => {
    setEnabled(isFeatureEnabled(flag))
    return onFeatureFlags(() => setEnabled(isFeatureEnabled(flag)))
  }, [flag])
  return enabled
}

// Multivariate flag: the variant key (string), true/false, or undefined while
// the flag has not resolved yet. With `enabled` false the flag is never read,
// which matters for experiments: reading a flag is what PostHog counts as
// exposure ($feature_flag_called), so a visitor outside the audience must not
// trigger the read at all (SPEC-102 FR-4).
export const useFeatureFlagVariant = (flag, { enabled = true } = {}) => {
  const [variant, setVariant] = useState(() =>
    enabled ? getFeatureFlag(flag) : undefined
  )
  useEffect(() => {
    if (!enabled) {
      setVariant(undefined)
      return undefined
    }
    setVariant(getFeatureFlag(flag))
    return onFeatureFlags(() => setVariant(getFeatureFlag(flag)))
  }, [flag, enabled])
  return variant
}

// True once flags have landed (onFeatureFlags fires at once when they already
// have), or once PostHog's remote config has said there are no active flags
// to fetch (polled, posthog-js emits no event for it), or after `timeoutMs`
// when neither happens (PostHog blocked, slow network), or immediately when
// PostHog never initialised. Lets a page hold a layout decision for a moment
// instead of flashing the wrong one.
export const REMOTE_CONFIG_POLL_MS = 50

// `enabled` false (nobody is waiting for the answer) reads as ready at once
// and sets nothing up.
export const useFeatureFlagsReady = (
  timeoutMs = 1000,
  { enabled = true } = {}
) => {
  const [ready, setReady] = useState(
    () => !enabled || !isPosthogReady() || remoteConfigHasNoFlags()
  )
  useEffect(() => {
    if (ready || !enabled) return undefined
    let settled = false
    let unsubscribe = null
    const stop = () => {
      clearTimeout(timer) // eslint-disable-line no-use-before-define
      clearInterval(poll) // eslint-disable-line no-use-before-define
      if (unsubscribe) unsubscribe()
    }
    const done = () => {
      if (settled) return
      settled = true
      stop()
      setReady(true)
    }
    const timer = setTimeout(done, timeoutMs)
    const poll = setInterval(() => {
      if (remoteConfigHasNoFlags()) done()
    }, REMOTE_CONFIG_POLL_MS)
    // onFeatureFlags calls back at once when flags are already loaded, i.e.
    // before it has returned the unsubscribe; settle that case here
    unsubscribe = onFeatureFlags(done)
    if (settled) unsubscribe()
    return stop
  }, [enabled])
  return ready
}
